import AddSearchClient from "addsearch-js-client"
import AddSearchUI from "addsearch-search-ui"
import React, { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { type HelpArticle } from "@/help_center/components/help_articles"
import {
  searchfieldTemplate,
  searchresultsTemplate,
  noResultsTemplate,
  numberOfResultsTemplate,
} from "@/help_center/components/help_search_templates"
import "@/help_center/styles/help_search.css"

// https://github.com/AddSearch/search-ui-react-example

export interface HelpSearchProps {
  idPrefix: string
  updateArticle: (article: HelpArticle) => void
  allArticles: HelpArticle[]
}

interface SearchFieldProps {
  ui: any
  idPrefix: string
  searchFieldRef: React.RefObject<HTMLDivElement>
}

interface SearchResultsProps {
  ui: any
  idPrefix: string
  resultsRef: React.RefObject<HTMLDivElement>
}

const SearchField: React.FC<SearchFieldProps> = ({ ui, idPrefix, searchFieldRef }) => {
  const { t } = useTranslation()
  const id = `${idPrefix}-searchfield`

  useEffect(() => {
    ui.searchField({
      containerId: id,
      template: searchfieldTemplate(t),
      searchAsYouType: true,
    })
  }, [ui, id, t])

  return <div id={id} ref={searchFieldRef}></div>
}

const SearchResults: React.FC<SearchResultsProps> = ({ ui, idPrefix, resultsRef }) => {
  const { t } = useTranslation()
  const id = `${idPrefix}-results`

  useEffect(() => {
    ui.searchResults({
      containerId: id,
      template: searchresultsTemplate(idPrefix == "widget"),
      template_noresults: noResultsTemplate(t),
      template_resultcount: numberOfResultsTemplate(t),
    })
  }, [ui, id, t, idPrefix])

  return <div id={id} ref={resultsRef} className="hidden"></div>
}

const HelpSearch: React.FC<HelpSearchProps> = ({ idPrefix, updateArticle, allArticles }) => {
  const searchFieldRef = useRef<HTMLDivElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  const addSearchUI = useMemo(() => {
    const addSearchClient = new AddSearchClient("7025a3437545e541de2ba6a48d6bb0ee")

    addSearchClient.setCategoryFilters(
      `${document.location.host}/help_center/help_article,${document.location.host}/help_center/help_question`
    )

    const ui = new AddSearchUI(addSearchClient, { updateBrowserHistory: false })
    ui.start()

    return ui
  }, [])

  useEffect(() => {
    const closeBtn = document.querySelector(`#${idPrefix}-searchfield .close-icon span`) as HTMLElement
    const handleKeyUp = (e: MouseEvent) => {
      if (searchFieldRef.current?.contains(e.target as HTMLElement)) {
        const input = e.target as HTMLInputElement

        if (input) {
          if (input.value.length == 0) {
            closeBtn?.classList.add("hidden")
            resultsRef.current?.classList.add("hidden")
          } else {
            closeBtn?.classList.remove("hidden")
            resultsRef.current?.classList.remove("hidden")
          }
        }
      }
    }

    document.addEventListener("keyup", handleKeyUp)

    return () => {
      document.removeEventListener("keyup", handleKeyUp)
    }
  }, [idPrefix])

  useEffect(() => {
    const closeBtn = document.querySelector(`#${idPrefix}-searchfield .close-icon span`) as HTMLElement
    const handleSearchClose = (e: MouseEvent) => {
      if (closeBtn?.contains(e.target as HTMLElement)) {
        addSearchUI.search("")
        closeBtn?.classList.add("hidden")
        resultsRef.current?.classList.add("hidden")
      }
    }

    document.addEventListener("click", handleSearchClose)

    return () => {
      document.removeEventListener("click", handleSearchClose)
    }
  })

  useEffect(() => {
    const handleArticleClick = (e: MouseEvent) => {
      if (idPrefix == "widget" && e.target instanceof Element) {
        const hit = e.target.closest(".hit.article") as HTMLElement
        if (hit) {
          const helpArticleId = hit.querySelector("a")?.dataset.helpArticleId
          const article = allArticles.filter(article => article.id == helpArticleId)[0]

          if (article) {
            updateArticle(article)
          }
        }
      }
    }

    document.addEventListener("click", handleArticleClick)

    return () => {
      document.removeEventListener("click", handleArticleClick)
    }
  }, [idPrefix, allArticles, updateArticle])

  return (
    <div id="helpcenter_search">
      <SearchField ui={addSearchUI} idPrefix={idPrefix} searchFieldRef={searchFieldRef} />
      <SearchResults ui={addSearchUI} idPrefix={idPrefix} resultsRef={resultsRef} />
    </div>
  )
}

export default HelpSearch
